import { captureException, init, setUser } from '@sentry/react'
import { userStorage } from '../services/user-storage'
import type User from '../types/api/user'

export { ErrorBoundary } from '@sentry/react'

type UiErrorLike = {
    status?: number | string
    data?: unknown
}

function extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message
    }

    if (!error || typeof error !== 'object') {
        return String(error ?? 'Unknown error')
    }

    const maybeRtk = error as UiErrorLike & { data?: { error?: { message?: string }; message?: string } }
    const nestedMessage = maybeRtk.data?.error?.message ?? maybeRtk.data?.message

    if (typeof nestedMessage === 'string' && nestedMessage) {
        return nestedMessage
    }

    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
        return (error as { message: string }).message
    }

    try {
        return JSON.stringify(error)
    } catch {
        return 'Unknown error'
    }
}

function extractHttpStatus(error: unknown): string | undefined {
    if (!error || typeof error !== 'object' || !('status' in error)) {
        return undefined
    }

    const status = (error as UiErrorLike).status
    return status === undefined ? undefined : String(status)
}

export function initGlitchTip() {
    if (process.env.NODE_ENV === 'development') {
        return
    }

    const dsn = process.env.REACT_APP_GLITCHTIP_DSN

    if (!dsn) {
        return
    }

    init({
        dsn,
        environment: process.env.NODE_ENV,
        // Должен совпадать с release при npm run upload:sourcemaps
        release: process.env.REACT_APP_VERSION || 'g-frontend@dev',
        // GlitchTip: только ошибки; без replay/profiling/session health
        tracesSampleRate: 0,
        sendDefaultPii: false,
        integrations: integrations =>
            integrations.filter(integration => integration.name !== 'BrowserSession'),
    })

    const storedUser = userStorage.get()
    if (storedUser) {
        setGlitchTipUser(storedUser)
    }
}

export function setGlitchTipUser(user: Pick<User, 'id' | 'username'> | null) {
    if (!user) {
        setUser(null)
        return
    }

    setUser({
        id: String(user.id),
        username: user.username,
    })
}

/** Репорт ошибки из UI-модалки (useErrorModal) в GlitchTip. */
export function reportUiError(title: string, error: unknown) {
    const message = extractErrorMessage(error)
    const httpStatus = extractHttpStatus(error)
    const route =
        typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : undefined

    const tags: Record<string, string> = {
        source: 'ui-error-modal',
        modal_title: title,
    }

    if (httpStatus !== undefined) {
        tags.http_status = httpStatus
    }

    if (route) {
        tags.route = route
    }

    captureException(new Error(`${title}: ${message}`), {
        tags,
        contexts: {
            ...(route ? { route: { path: route } } : {}),
            apiError: {
                status: httpStatus ?? null,
                data: error && typeof error === 'object' && 'data' in error ? (error as UiErrorLike).data : error,
            },
        },
        fingerprint: ['ui-error-modal', title, httpStatus ?? '', message],
    })
}

/** Явная отправка тестовой ошибки (unit / DevTools). */
export function reportGlitchTipTestError() {
    captureException(new Error('GlitchTip frontend test error'))
}
